import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import { 
  ClipboardList, 
  Package, 
  Clock, 
  CheckCircle, 
  ChevronRight,
  MapPin,
  Calendar,
  CreditCard,
  XCircle,
  Truck,
  Coffee,
  ShoppingBag,
  AlertCircle,
  Download,
  FileText
} from 'lucide-react';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from 'react-i18next';
import api, { BASE_URL } from '../services/api';

const Orders = () => {
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);
  const navigate = useNavigate();

  const fetchOrders = async () => {
    try {
      const res = await api.get('/orders/my-orders');
      setOrders(res.data.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (orderId) => {
    try {
      setDownloadingInvoice(orderId);
      const response = await api.get(`/orders/${orderId}/invoice`, {
        responseType: 'blob'
      });
      
      // Create a link to download the file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${orderId.slice(-6)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Invoice download failed:', err);
      alert('Failed to download invoice');
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const renderItemName = (name) => {
    if (!name) return '';
    
    let nameObj = name;
    if (typeof name === 'string') {
      if (name.startsWith('{')) {
        try {
          // Attempt to parse after fixing potential single quotes (invalid JSON)
          const fixedJson = name.replace(/'/g, '"');
          nameObj = JSON.parse(fixedJson);
        } catch (e) {
          // If parsing fails, try regex extraction for the English field
          const enMatch = name.match(/en:\s*['"](.*?)['"]/);
          if (enMatch && enMatch[1]) return enMatch[1];
          return name;
        }
      } else {
        return name;
      }
    }
    
    // Always return English as requested
    return nameObj.en || nameObj.ar || '';
  };

  useEffect(() => {
    if (!user) {
      navigate('/login?redirect=/orders');
      return;
    }
    fetchOrders();
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;

    // Connect to socket
    console.log('[SOCKET] Connecting to:', BASE_URL);
    const socket = io(BASE_URL, {
      transports: ['websocket', 'polling'],
      withCredentials: true
    });

    socket.on('connect', () => {
      console.log('[SOCKET] Connected with ID:', socket.id);
      // Join user room
      const roomId = user.id || user._id;
      console.log('[SOCKET] Joining room:', roomId);
      socket.emit('join', roomId);
    });

    socket.on('connect_error', (err) => {
      console.error('[SOCKET] Connection Error:', err.message);
    });

    // Listen for status updates
    socket.on('orderStatusUpdated', (updatedOrder) => {
      console.log('[SOCKET] Received order update:', updatedOrder);
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order._id === updatedOrder._id ? updatedOrder : order
        )
      );
    });

    return () => {
      console.log('[SOCKET] Disconnecting...');
      socket.disconnect();
    };
  }, [user]);

  const getStatusStyle = (status) => {
    switch (status) {
      case 'pending':
        return { color: '#EAB308', bg: '#FEF9C3', label: 'Pending Approval' };
      case 'awaiting_payment':
        return { color: '#F97316', bg: '#FFEDD5', label: 'Awaiting Payment' };
      case 'paid':
        return { color: '#0D9488', bg: '#CCFBF1', label: 'Payment Received' };
      case 'confirmed':
        return { color: '#2563EB', bg: '#DBEAFE', label: 'Order Confirmed' };
      case 'preparing':
        return { color: '#9333EA', bg: '#F3E8FF', label: 'Brewing Your Coffee' };
      case 'ready':
        return { color: '#16A34A', bg: '#DCFCE7', label: 'Ready for Pickup' };
      case 'delivering':
        return { color: '#6366F1', bg: '#EEF2FF', label: 'Out for Delivery' };
      case 'delivered':
        return { color: '#4B5563', bg: '#F3F4F6', label: 'Delivered' };
      case 'cancelled':
        return { color: '#DC2626', bg: '#FEE2E2', label: 'Order Cancelled' };
      default:
        return { color: theme.text, bg: theme.surface, label: status };
    }
  };

  const handleCancelOrder = async (id) => {
    if (window.confirm('Are you sure you want to cancel this order?')) {
      try {
        await api.put(`/orders/${id}/cancel`);
        fetchOrders();
      } catch (err) {
        alert(err.response?.data?.message || 'Failed to cancel order');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.background }}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: theme.primary }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 md:py-20" style={{ backgroundColor: theme.background }}>
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="flex items-center space-x-4 mb-12">
          <div className="p-4 rounded-3xl" style={{ backgroundColor: theme.primary }}>
            <ClipboardList size={28} style={{ color: theme.background }} />
          </div>
          <div>
            <h1 className="text-4xl font-black uppercase tracking-tighter" style={{ color: theme.text }}>
              History
            </h1>
            <p className="text-sm font-bold uppercase tracking-widest opacity-40">Your coffee journey</p>
          </div>
        </div>

        {orders.length === 0 ? (
          <div className="text-center py-20 rounded-[40px] border-2 border-dashed" style={{ borderColor: theme.border }}>
            <Coffee size={64} className="mx-auto mb-6 opacity-10" style={{ color: theme.text }} />
            <h2 className="text-2xl font-black uppercase tracking-widest" style={{ color: theme.text }}>No orders yet</h2>
            <p className="mt-4 font-bold opacity-40 mb-8" style={{ color: theme.text }}>Start your coffee adventure today!</p>
            <Link 
              to="/products"
              className="inline-block px-10 py-4 rounded-2xl font-black uppercase tracking-widest shadow-2xl transition-transform active:scale-95"
              style={{ backgroundColor: theme.primary, color: theme.background }}
            >
              Order Now
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:gap-8">
            {orders.map((order) => {
              const style = getStatusStyle(order.status);
              return (
                <div 
                  key={order._id}
                  className="rounded-[40px] border-2 transition-all duration-500 hover:shadow-2xl overflow-hidden group"
                  style={{ backgroundColor: theme.card, borderColor: theme.border }}
                >
                  <div className="p-6 md:p-10">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                      <div className="flex items-center space-x-4">
                        <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-inner" style={{ backgroundColor: style.bg }}>
                          <Package size={24} style={{ color: style.color }} />
                        </div>
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-widest opacity-40 mb-1">Order ID</p>
                          <p className="text-sm font-mono font-bold" style={{ color: theme.text }}>#{order._id.slice(-8).toUpperCase()}</p>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-3">
                        <span 
                          className="px-6 py-2 rounded-full font-black uppercase tracking-widest text-[10px] shadow-sm"
                          style={{ backgroundColor: style.bg, color: style.color }}
                        >
                          {style.label}
                        </span>
                        {order.orderType === 'delivery' ? (
                          <span className="flex items-center space-x-2 px-6 py-2 rounded-full font-black uppercase tracking-widest text-[10px] border-2" style={{ borderColor: theme.border, color: theme.text }}>
                            <Truck size={14} />
                            <span>Delivery</span>
                          </span>
                        ) : (
                          <span className="flex items-center space-x-2 px-6 py-2 rounded-full font-black uppercase tracking-widest text-[10px] border-2" style={{ borderColor: theme.border, color: theme.text }}>
                            <ShoppingBag size={14} />
                            <span>Pickup</span>
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12 pb-8 mb-8 border-b-2 border-dashed" style={{ borderColor: theme.border }}>
                      {/* Items */}
                      <div className="lg:col-span-2">
                        <div className="space-y-4">
                          {order.orderItems.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between p-4 rounded-2xl bg-opacity-5 transition-colors group-hover:bg-opacity-10" style={{ backgroundColor: theme.surface }}>
                              <div className="flex items-center space-x-4">
                                <div className="w-12 h-12 rounded-xl overflow-hidden border-2" style={{ borderColor: theme.border }}>
                                  <img 
                                    src={item.image ? (item.image.startsWith('http') ? item.image : `${BASE_URL}${item.image}`) : 'https://images.unsplash.com/photo-1559056199-641a0ac8b55e?q=80&w=100&auto=format&fit=crop'} 
                                    alt={renderItemName(item.name)} 
                                    className="w-full h-full object-cover" 
                                  />
                                </div>
                                <div>
                                  <p className="font-black uppercase tracking-tight text-sm" style={{ color: theme.text }}>
                                    {item.quantity}x {renderItemName(item.name)}
                                  </p>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    <p className="text-[10px] font-bold opacity-40 uppercase">{item.variant?.name || 'Standard'}</p>
                                    {item.selectedAddOns?.map((addOn, aIdx) => (
                                      <p key={aIdx} className="text-[10px] font-bold opacity-40 uppercase">
                                        • {renderItemName(addOn.name)}
                                      </p>
                                    ))}
                                  </div>
                                </div>
                              </div>
                              <p className="font-black text-sm" style={{ color: theme.primary }}>${(item.priceAtPurchase * item.quantity).toFixed(2)}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Summary */}
                      <div className="space-y-6">
                        <div className="space-y-3">
                          <div className="flex items-center space-x-2 opacity-40">
                            <MapPin size={14} />
                            <span className="text-[10px] font-black uppercase tracking-widest">{t('common.branch')}: {order.branch?.name || t('common.all')}</span>
                          </div>
                          <div className="flex items-center space-x-2 opacity-40">
                            <Calendar size={14} />
                            <span className="text-[10px] font-black uppercase tracking-widest">{new Date(order.createdAt).toLocaleDateString()} at {new Date(order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                          {order.orderType === 'delivery' && (
                            <div className="flex items-start space-x-2 opacity-40">
                              <MapPin size={14} className="mt-0.5" />
                              <span className="text-[10px] font-black uppercase tracking-widest leading-relaxed">
                                {order.shippingAddress?.street}, {order.shippingAddress?.city}
                              </span>
                            </div>
                          )}
                          <div className="flex items-center space-x-2 opacity-40">
                            <CreditCard size={14} />
                            <span className="text-[10px] font-black uppercase tracking-widest">Paid via {order.paymentMethod.toUpperCase()}</span>
                          </div>
                        </div>

                        <div className="p-6 rounded-3xl" style={{ backgroundColor: theme.surface }}>
                          <div className="flex justify-between items-end">
                            <p className="text-[10px] font-black uppercase tracking-widest opacity-40 mb-1">Grand Total</p>
                            <p className="text-3xl font-black" style={{ color: theme.primary }}>${order.totalPrice.toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                      <div className="flex items-center space-x-2 px-4 py-2 rounded-xl bg-opacity-5" style={{ backgroundColor: theme.primary }}>
                        <Clock size={14} style={{ color: theme.primary }} />
                        <p className="text-[10px] font-black uppercase tracking-widest" style={{ color: theme.primary }}>Estimated: {order.estimatedTime || 20} mins</p>
                      </div>

                      <div className="flex items-center space-x-4 w-full sm:w-auto">
                        {order.status !== 'cancelled' && (
                          <button 
                            onClick={() => handleDownloadInvoice(order._id)}
                            disabled={downloadingInvoice === order._id}
                            className="flex-1 sm:flex-none flex items-center justify-center space-x-2 px-6 py-3 rounded-xl font-black uppercase tracking-widest text-[10px] border-2 transition-all active:scale-95 disabled:opacity-50"
                            style={{ borderColor: theme.border, color: theme.text }}
                          >
                            {downloadingInvoice === order._id ? (
                              <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Download size={14} />
                            )}
                            <span>{t('common.invoice')}</span>
                          </button>
                        )}
                        <Link 
                          to={`/orders`}
                          className="flex-1 sm:flex-none px-8 py-3 rounded-xl font-black uppercase tracking-widest text-[10px] border-2 transition-all active:scale-95 text-center"
                          style={{ borderColor: theme.border, color: theme.text }}
                        >
                          {t('common.details')}
                        </Link>
                        {['pending', 'awaiting_payment'].includes(order.status) && (
                          <button 
                            onClick={() => handleCancelOrder(order._id)}
                            className="flex-1 sm:flex-none px-8 py-3 rounded-xl font-black uppercase tracking-widest text-[10px] bg-red-500 text-white shadow-lg shadow-red-500/20 active:scale-95 transition-all"
                          >
                            Cancel Order
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;
